const TRUCK7_API_BASE = "https://truck7.eu/api/v1/marketing";

/**
 * Fetch Truck7 listings for promotional banners
 * @param {number} limit - Number of listings to fetch (1-20)
 * @returns {Promise<Array>} - Array of truck listings
 */
export const fetchTruck7Listings = async (limit = 5) => {
  try {
    const response = await fetch(
      `${TRUCK7_API_BASE}/listings?limit=${Math.min(Math.max(limit, 1), 20)}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching Truck7 listings:", error);
    // Return empty array on error to avoid breaking the UI
    return [];
  }
};

/**
 * Format price for display
 * @param {string|number} price - Price value
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price) => {
  if (!price || price === "0" || price === "0.00") {
    return "Price on request";
  }

  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    return "Price on request";
  }

  return `€${numPrice.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

/**
 * Format vehicle registration information
 * @param {Object} attributes - Vehicle attributes object
 * @returns {string} - Formatted registration year/month
 */
export const formatRegistration = (attributes) => {
  if (!attributes || !attributes.first_registration_year) {
    return "N/A";
  }

  const year = attributes.first_registration_year;
  const month = attributes.first_registration_month;

  if (month) {
    return `${month}/${year}`;
  }

  return year.toString();
};

/**
 * Format mileage for display
 * @param {Object} attributes - Vehicle attributes object
 * @returns {string} - Formatted mileage string
 */
export const formatMileage = (attributes) => {
  if (!attributes || !attributes.mileage_km) {
    return "N/A";
  }

  const mileage = parseInt(attributes.mileage_km);
  if (isNaN(mileage)) {
    return "N/A";
  }

  return `${mileage.toLocaleString("en-US")} km`;
};

/**
 * Get country flag emoji
 * @param {string} countryCode - Two-letter country code
 * @returns {string} - Flag emoji or country code
 */
export const getCountryFlag = (countryCode) => {
  const flags = {
    NL: "🇳🇱",
    DE: "🇩🇪",
    BE: "🇧🇪",
    FR: "🇫🇷",
    IT: "🇮🇹",
    ES: "🇪🇸",
    PL: "🇵🇱",
    CZ: "🇨🇿",
    SK: "🇸🇰",
    AT: "🇦🇹",
    HU: "🇭🇺",
    RO: "🇷🇴",
    BG: "🇧🇬",
    HR: "🇭🇷",
    SI: "🇸🇮",
    DK: "🇩🇰",
    SE: "🇸🇪",
    NO: "🇳🇴",
    FI: "🇫🇮",
    EE: "🇪🇪",
    LV: "🇱🇻",
    LT: "🇱🇹",
    GB: "🇬🇧",
    IE: "🇮🇪",
    PT: "🇵🇹",
    CH: "🇨🇭",
    LU: "🇱🇺",
    MT: "🇲🇹",
    CY: "🇨🇾",
    GR: "🇬🇷",
  };

  return flags[countryCode?.toUpperCase()] || countryCode;
};

const truck7Service = {
  fetchTruck7Listings,
  formatPrice,
  formatRegistration,
  formatMileage,
  getCountryFlag,
};

export default truck7Service;
